import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  BasicInfoCard,
  AddedComponentsCard,
  NewComponentConfigCard,
  AvailableComponentsCard,
  ReportSectionCard,
  ReportFinalizeActions,
} from '@/pages/reports/new/components';
import { useReportNew } from './hooks/useReportNew';
import { AutoSaveIndicator } from '@/pages/reports/edit/components/AutoSaveIndicator';

export const ReportNewPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    title,
    titleError,
    edition,
    editionError,
    onEditionChange,
    components,
    selectedComponent,
    componentProps,
    validationErrors,
    editingComponentId,
    isSaving,
    isSavingBasicInfo,
    partnerId,
    partnerError,
    lang,
    langError,
    componentsError,
    partnerOptions,
    handleTitleChange,
    handleAddComponent,
    handleEditComponent,
    handleSaveComponent,
    handleRemoveComponent,
    handleMoveComponent,
    handlePropChange,
    handleSaveBasicInfo,
    handleFinalizeClose,
    handleFinalizeSend,
    cancelEdit,
    hasSavedBasicInfo,
    openSection,
    handleSectionOpenChange,
    onPartnerChange,
    onLangChange,
    thematicTemplates,
    selectedTemplateId,
    onTemplateSelect,
    onClearAll,
    onSaveTemplate,
    onUpdateTemplate,
    onDeleteTemplate,
    autoSaveStatus,
    lastSavedAt,
  } = useReportNew();

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <div
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </div>
          </div>
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3 gap-4">
              <h1 className="text-4xl font-bold text-gray-900">Novo report</h1>
              <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} />
            </div>
            <p className="text-lg text-gray-600">
              Construa seu report adicionando e configurando componentes
            </p>
          </div>
          <div className="space-y-6">
            <div id="report-basic-section">
              <ReportSectionCard
                title="Dados do report"
                description="Informações básicas para identificar o report"
                open={openSection === 'basic'}
                onOpenChange={(open) => handleSectionOpenChange('basic', open)}
                completed={hasSavedBasicInfo}
              >
                <BasicInfoCard
                  title={title}
                  titleError={titleError}
                  onTitleChange={handleTitleChange}
                  edition={edition}
                  editionError={editionError}
                  onEditionChange={onEditionChange}
                  partnerId={partnerId}
                  partnerError={partnerError}
                  onPartnerChange={onPartnerChange}
                  partnerOptions={partnerOptions}
                  lang={lang}
                  langError={langError}
                  onLangChange={onLangChange}
                  showActions
                  isSaving={isSavingBasicInfo}
                  onCancel={() => navigate('/app/reports')}
                  onSaveContinue={handleSaveBasicInfo}
                />
              </ReportSectionCard>
            </div>

            <div id="report-components-section">
              <ReportSectionCard
                title="Componentes"
                description="Adicione e configure os blocos de conteúdo do seu report"
                open={openSection === 'components'}
                onOpenChange={(open) => handleSectionOpenChange('components', open)}
                locked={!hasSavedBasicInfo}
              >
                <div className="space-y-8">
                  <AddedComponentsCard
                    components={components}
                    editingComponentId={editingComponentId}
                    selectedComponent={selectedComponent}
                    componentProps={componentProps}
                    validationErrors={validationErrors}
                    onEditComponent={handleEditComponent}
                    onRemoveComponent={handleRemoveComponent}
                    onMoveComponent={handleMoveComponent}
                    onPropChange={handlePropChange}
                    onSaveComponent={handleSaveComponent}
                    onCancelEdit={cancelEdit}
                    thematicTemplates={thematicTemplates}
                    selectedTemplateId={selectedTemplateId}
                    onTemplateSelect={onTemplateSelect}
                    onClearAll={onClearAll}
                    onSaveTemplate={onSaveTemplate}
                    onUpdateTemplate={onUpdateTemplate}
                    onDeleteTemplate={onDeleteTemplate}
                  />
                  {selectedComponent && !editingComponentId && (
                    <NewComponentConfigCard
                      selectedComponent={selectedComponent}
                      componentProps={componentProps}
                      validationErrors={validationErrors}
                      onPropChange={handlePropChange}
                      onSave={handleSaveComponent}
                      onCancel={cancelEdit}
                      thematicTemplates={thematicTemplates}
                      selectedTemplateId={selectedTemplateId}
                      onTemplateSelect={onTemplateSelect}
                      onSaveTemplate={onSaveTemplate}
                      onUpdateTemplate={onUpdateTemplate}
                      onDeleteTemplate={onDeleteTemplate}
                    />
                  )}
                  <AvailableComponentsCard
                    onAddComponent={handleAddComponent}
                    hideHeader
                    disabledTypes={
                      components.some((component) => component.type === 'hero')
                        ? ['hero']
                        : []
                    }
                  />
                </div>
              </ReportSectionCard>
            </div>

            {hasSavedBasicInfo && (
              <ReportFinalizeActions
                isSaving={isSaving}
                onFinalizeClose={handleFinalizeClose}
                onFinalizeSend={handleFinalizeSend}
                errorMessage={componentsError}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
